import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowLeft,
  Copy,
  Gift,
  ReceiptText,
  ShieldCheck,
  TicketPercent,
} from "lucide-react";
import { AppDispatch, RootState } from "../../../../store";
import { getShoppingVouchers } from "../../../../Redux/referral/slice";
import {
  formatVoucherCurrency,
  formatVoucherDate,
  formatVoucherLongDate,
  mapVoucherItemToRecord,
  toClassSlug,
} from "../stuffAdapters";
import "./ShoppingVoucherPage.css";

const ShoppingVoucherDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { shoppingVouchers } = useSelector(
    (state: RootState) => state.referral,
  );
  const [copied, setCopied] = useState(false);

  const item = shoppingVouchers.data?.find(
    (v) => v._id === id || v.voucherId === id,
  );

  useEffect(() => {
    if (!item && !shoppingVouchers.loading) {
      dispatch(
        getShoppingVouchers({
          page: 1,
          limit: 50,
          status: "ALL",
          source: "ALL",
          sortOrder: "DESC",
        }),
      );
    }
  }, [dispatch, item, shoppingVouchers.loading]);

  const copyVoucherCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1300);
    } catch {
      setCopied(false);
    }
  };

  const voucher = item ? mapVoucherItemToRecord(item) : null;
  const loading = shoppingVouchers.loading && !voucher;

  return (
    <section className="shopping-voucher-page">
      <section className="shopping-voucher-sheet">
        <header className="shopping-voucher-sheet-header">
          <div>
            <span>VOUCHER DETAILS</span>
            <h2 id="shopping-voucher-sheet-title">
              Salar Shopping Voucher
            </h2>
          </div>
          <button
            type="button"
            onClick={() => navigate("/SalarShopVoucher")}
            aria-label="Back to vouchers"
          >
            <ArrowLeft size={19} strokeWidth={2} />
          </button>
        </header>

        {loading ? (
          <div className="shopping-voucher-empty">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : !voucher ? (
          <div className="shopping-voucher-empty">
            <Gift size={34} strokeWidth={1.8} />
            <strong>Voucher not found</strong>
            <span>This voucher may have been removed.</span>
          </div>
        ) : (
          <>
            <div className="shopping-voucher-sheet-status">
              <span
                className={`shopping-voucher-status shopping-voucher-status-${toClassSlug(
                  voucher.status,
                )}`}
              >
                {voucher.status}
              </span>
              <span>
                <ReceiptText size={14} strokeWidth={2} />
                {item?.voucherId || voucher.code}
              </span>
            </div>

            <div className="shopping-voucher-details shopping-voucher-details-sheet">
              <div className="shopping-voucher-detail-main">
                <div className="shopping-voucher-detail-image">
                  <img src={voucher.image} alt={voucher.title} />
                </div>
                <div className="shopping-voucher-detail-copy">
                  <span className="shopping-voucher-detail-kicker">
                    PURCHASED ON {formatVoucherLongDate(voucher.purchasedOn)}
                  </span>
                  <h2>{voucher.title}</h2>
                  <p>
                    {voucher.orderNo !== "-" ? `Order ${voucher.orderNo} · ` : ""}
                    {voucher.paymentType} · {voucher.description}
                  </p>
                  <div className="shopping-voucher-detail-actions">
                    <button type="button" className="primary">
                      <TicketPercent size={16} strokeWidth={2} />
                      Redeem voucher
                    </button>
                  </div>
                </div>
              </div>

              <div className="shopping-voucher-facts">
                <div>
                  <span>Grand Total</span>
                  <strong>{formatVoucherCurrency(voucher.grandTotal)}</strong>
                </div>
                <div>
                  <span>Salar Shopping Voucher</span>
                  <strong>{formatVoucherCurrency(voucher.voucherValue)}</strong>
                </div>
                <div>
                  <span>Valid From</span>
                  <strong>{formatVoucherDate(voucher.validFrom)}</strong>
                </div>
                <div>
                  <span>Valid To</span>
                  <strong>{formatVoucherDate(voucher.validTo)}</strong>
                </div>
              </div>

              <div className="shopping-voucher-code-card">
                <div>
                  <span>VOUCHER CODE</span>
                  <strong>{voucher.code}</strong>
                </div>
                <button
                  type="button"
                  onClick={() => copyVoucherCode(voucher.code)}
                >
                  <Copy size={16} strokeWidth={2} />
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
          </>
        )}

        <div className="shopping-voucher-sheet-safe">
          <ShieldCheck size={16} strokeWidth={2} />
          Voucher details are protected within your Salar account.
        </div>
      </section>
    </section>
  );
};

export default ShoppingVoucherDetails;
